import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchMidpointMediaPeopleDashboard } from "../../../api/midpointMediaAdminApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../../components/clientPortal/ClientPortalStates";
import useMidpointMediaAdminView from "../../../hooks/useMidpointMediaAdminView";
import { formatClientDateTime } from "../../../utils/clientPortalFormat";
import "../../../styles/midpointMediaAdmin.css";

export default function MidpointMediaAdminPeoplePage() {
  const { clientViewId, paths } = useMidpointMediaAdminView();
  const [state, setState] = useState({ loading: true, error: "", dashboard: null });

  useEffect(() => {
    if (!clientViewId) return undefined;
    let cancelled = false;
    fetchMidpointMediaPeopleDashboard(clientViewId)
      .then((body) => {
        if (!cancelled) {
          setState({ loading: false, error: "", dashboard: body.dashboard || null });
        }
      })
      .catch((error) => {
        if (!cancelled) setState({ loading: false, error: error.message, dashboard: null });
      });
    return () => {
      cancelled = true;
    };
  }, [clientViewId]);

  if (!clientViewId) return <p className="empty-state">No workspace assigned.</p>;
  if (state.loading) return <ClientPortalLoading message="Loading people workspace…" />;
  if (state.error && !state.dashboard) return <ClientPortalError message={state.error} />;

  const dashboard = state.dashboard || {};
  const summary = dashboard.summary || {};
  const actions = dashboard.actions || [];
  const sections = dashboard.sections || {};
  const recent = dashboard.recentAwaitingReview || [];

  return (
    <div className="mm-admin-page">
      <header>
        <p className="mm-admin-kicker">People</p>
        <h1>Hiring & coverage</h1>
        <p className="mm-admin-lead">
          Review Venezuela careers applications, keep operator accounts ready, and confirm who is
          covering interviews this week.
        </p>
      </header>

      {state.error ? <ClientPortalError message={state.error} /> : null}

      <section className="mm-admin-kpi-grid" aria-label="People summary">
        <article className="mm-admin-card">
          <span className="muted">Awaiting review</span>
          <div className="mm-admin-kpi">
            {summary.awaitingReview ?? 0}
            <span>New applications</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <span className="muted">In conversation</span>
          <div className="mm-admin-kpi">
            {summary.inConversation ?? 0}
            <span>Contacted / qualified</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <span className="muted">Active accounts</span>
          <div className="mm-admin-kpi">
            {summary.accountsActive ?? 0}
            <span>{summary.accountsInvited || 0} invited</span>
          </div>
        </article>
        <article className="mm-admin-card">
          <span className="muted">Applications</span>
          <div className="mm-admin-kpi">
            {summary.applicationsTotal ?? 0}
            <span>{summary.missingResume || 0} missing résumé</span>
          </div>
        </article>
      </section>

      <section className="mm-admin-card">
        <h2>Do next</h2>
        <div className="mm-admin-action-list">
          {actions.map((action) => (
            <article key={action.id} className={`mm-admin-action mm-admin-action--${action.tone || "info"}`}>
              <div>
                <strong>{action.title}</strong>
                <p>{action.detail}</p>
              </div>
              <Link className="btn btn-primary" to={`${paths.base}/${action.href}`}>
                {action.cta || "Open"}
              </Link>
            </article>
          ))}
        </div>
      </section>

      <section className="mm-admin-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        {["applications", "accounts", "schedules"].map((key) => {
          const section = sections[key];
          if (!section) return null;
          return (
            <article key={key} className="mm-admin-card">
              <h2>{section.title}</h2>
              <div className="mm-admin-kpi" style={{ fontSize: "1.15rem" }}>
                {section.metric}
              </div>
              <p className="mm-admin-lead">{section.detail}</p>
              <Link className="btn btn-secondary" to={`${paths.base}/${section.href}`}>
                Open {section.title.toLowerCase()}
              </Link>
            </article>
          );
        })}
      </section>

      <section className="mm-admin-card">
        <div className="mm-admin-card__row">
          <h2>New applications queue</h2>
          <Link to={`${paths.base}/people/applications?status=new`}>View all new</Link>
        </div>
        {!recent.length ? (
          <p className="empty-state">No new applications waiting. Nice — check mid-pipeline next.</p>
        ) : (
          <table className="mm-admin-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Role</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((application) => (
                <tr key={application.id}>
                  <td>
                    <Link to={`${paths.base}/people/applications`}>
                      {application.name}
                    </Link>
                    <div className="muted">{application.email || "—"}</div>
                  </td>
                  <td>{application.roleType || "—"}</td>
                  <td>{formatClientDateTime(application.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
